import React from "react";

import { Hourglass, Download, AlertTriangle } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import { Section, BarRow, BarsSkeleton, EmptyNote, KpiTile, Collapsible, CHART, badRateColor, fmtDays, ExportButtons } from "./primitives";

// F2 — bottleneck & delay analysis. Data's job: compare "how long" across a few
// named stages (→ horizontal bars, the clearest magnitude compare), show where
// work piles up now (→ a compact WIP funnel), and how the queue ages vs SLA
// (→ a single 100%-stacked aging bar + a breach KPI). Distinct forms from F1.

const AGING_COLORS = { d0_2: CHART.green, d3_7: CHART.amber, d8_plus: CHART.red };

const BottlenecksSection = ({ data, loading, onExportCsv, onExportPdf, exporting }) => {
  const stages = data?.stageDurations ?? [];
  const maxStage = Math.max(1, ...stages.map((s) => s.p90Days || 0));

  const wip = data?.wip ?? [];
  const maxWip = Math.max(1, ...wip.map((w) => w.count || 0));

  const aging = data?.reviewQueueAging ?? [];
  const agingTotal = aging.reduce((s, b) => s + (b.count || 0), 0);

  return (
    <Section
      icon={Hourglass}
      title="Bottleneck & delay analysis"
      subtitle="Where does the process get held back?"
      action={<ExportButtons onExportCsv={onExportCsv} onExportPdf={onExportPdf} loading={loading} busy={exporting} />}
    >
      {/* Headline band: SLA + aging + re-request drag — expanded by default */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiTile
          label="Documents breaching SLA"
          value={loading ? undefined : (data?.slaBreachCount ?? 0)}
          color={badRateColor(data?.slaBreachRate ?? 0)}
          loading={loading}
          hint={`${data?.slaBreachRate ?? 0}% of the review queue · SLA ${data?.slaDays ?? "—"}d`}
        />
        <KpiTile
          label="Re-request drag"
          value={
            data?.rerequestDragDays !== null && data?.rerequestDragDays !== undefined ? fmtDays(data.rerequestDragDays) : data?.rerequestDragReason ? "—" : undefined
          }
          color={CHART.violet}
          loading={loading}
          hint={data?.rerequestDragReason ?? "extra time when a re-request is needed"}
        />
        {/* 100%-stacked aging bar */}
        <div className="rounded-xl p-3.5 bg-background-tertiary border border-border-primary flex flex-col justify-between">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle size={13} className="text-text-primary/70" />
            <p className="text-[12px] font-medium text-text-primary">Review queue age</p>
          </div>
          {loading ? (
            <BarsSkeleton rows={1} />
          ) : agingTotal === 0 ? (
            <p className="text-[11px] text-text-secondary">Queue is empty.</p>
          ) : (
            <>
              <div className="flex h-5 w-full rounded-md overflow-hidden">
                {aging.map((b) =>
                  b.count > 0 ? (
                    <div
                      key={b.key}
                      title={`${b.label}: ${b.count}`}
                      style={{
                        width: `${(b.count / agingTotal) * 100}%`,
                        background: AGING_COLORS[b.key],
                      }}
                    />
                  ) : null,
                )}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {aging.map((b) => (
                  <span key={b.key} className="flex items-center gap-1 text-[10px] text-text-secondary">
                    <span className="w-2 h-2 rounded-sm" style={{ background: AGING_COLORS[b.key] }} />
                    {b.label}: {b.count}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail breakdown — collapsed by default */}
      <Collapsible title="Stage-duration breakdown & current WIP">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Left: stage-duration bars (median filled, p90 as the track ceiling) */}
          <div>
            <p className="text-[12px] font-medium text-text-primary mb-2">Time spent per stage (median, days)</p>
            {loading ? (
              <BarsSkeleton rows={4} />
            ) : stages.every((s) => !s.sampleSize) ? (
              <EmptyNote>Not enough completed transitions to measure yet.</EmptyNote>
            ) : (
              <div className="flex flex-col">
                {stages.map((s) => {
                  const isSlowest = data?.slowestStageKey === s.key;
                  return (
                    <BarRow
                      key={s.key}
                      label={isSlowest ? `${s.label} · slowest stage` : s.label}
                      sublabel={s.sampleSize ? `p90 ${fmtDays(s.p90Days)} · n=${s.sampleSize}` : "no data"}
                      pct={((s.medianDays || 0) / maxStage) * 100}
                      valueLabel={fmtDays(s.medianDays)}
                      color={isSlowest ? CHART.red : CHART.indigo}
                    />
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-text-secondary mt-1.5">
              The slowest stage (red) is the biggest holdup in range — it will not always be the officer-review step.
            </p>
          </div>

          {/* Right: current WIP funnel */}
          <div>
            <p className="text-[12px] font-medium text-text-primary mb-2">Where candidates sit right now</p>
            {loading ? (
              <BarsSkeleton rows={3} />
            ) : wip.every((w) => !w.count) ? (
              <EmptyNote>No in-progress candidates.</EmptyNote>
            ) : (
              <div className="flex flex-col">
                {wip.map((w) => (
                  <BarRow
                    key={w.key}
                    label={w.label}
                    sublabel={w.oldestAgeDays ? `oldest ${w.oldestAgeDays}d` : undefined}
                    pct={((w.count || 0) / maxWip) * 100}
                    valueLabel={w.count}
                    color={CHART.blue}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Collapsible>
    </Section>
  );
};

export default BottlenecksSection;
